import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { money } from '../../../utils/money';

/**
 * Billing → Payments → Record a manual payment (Backend
 * billing_service.admin_record_manual_payment).
 *
 * A customer paid outside the checkout — a Razorpay payment link, QR or
 * dashboard payment, or a bank transfer / invoice — and the money is not tied
 * to their account, so they read as "assigned" or Explorer. This ties it:
 *   1. the account by e-mail (its live membership is shown);
 *   2. how they paid: a Razorpay payment id, checked with Razorpay
 *      (captured, not already on file), or an outside payment with method,
 *      amount, currency, reference and date;
 *   3. the plan and the access: from today, from the payment date, or the
 *      live membership's own dates kept (it is only marked as paid);
 *   4. a note, and whether to send the paid welcome e-mail.
 * The member can add a card for auto-renewal later from Billing.
 *
 * Above the form: captured Razorpay payments no account holds (Backend
 * admin_unmatched_payments), each with a suggested account — named in the
 * payment's notes, or the same e-mail / phone as the payer. "Use" fills the
 * form and checks it; "Hide" takes a payment that is not a membership off
 * the list.
 */

const STATE_LABEL: Record<string, string> = {
  explorer: 'Explorer', trial_active: 'On trial', trial_lapsed: 'Trial ended',
  voyager_assigned: 'Member (assigned, not paid)', voyager: 'Member (paid)',
};
const SOURCE_LABEL: Record<string, string> = {
  manual: 'granted by the team', manual_payment: 'paid, recorded by the team',
  razorpay_order: 'one-time payment', razorpay_subscription: 'auto-renewing',
  trial: 'trial', trial_admin: 'trial granted by the team',
};

const fmtDay = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const input = 'h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';
const label = 'text-xs font-medium text-grayscale-1200';

const ManualPaymentCard: React.FC<{ onRecorded?: () => void }> = ({ onRecorded }) => {
  const uid = apiService.getStoredUserId() || '';
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<'razorpay' | 'outside'>('razorpay');
  const [payId, setPayId] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [reference, setReference] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [plan, setPlan] = useState('');
  const [start, setStart] = useState<'today' | 'payment_date' | 'keep_current'>('today');
  const [months, setMonths] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [preview, setPreview] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<any | null>(null);
  const [days, setDays] = useState(90);
  const [found, setFound] = useState<any | null>(null);

  const loadFound = useCallback(async () => {
    const r = await apiService.adminUnmatchedPayments(uid, days);
    setFound(r.success && r.data?.success ? r.data : { rows: [], error: r.error || 'Could not read Razorpay payments' });
  }, [uid, days]);
  useEffect(() => { loadFound(); }, [loadFound]);

  const check = async (asEmail?: string, asPay?: string) => {
    const em = (asEmail ?? email).trim();
    const pid = (asPay ?? (kind === 'razorpay' ? payId : '')).trim();
    setErr(''); setDone(null); setBusy(true);
    const r = await apiService.adminManualPaymentPreview(uid, em, pid);
    setBusy(false);
    if (!(r.success && r.data?.success)) { setPreview(null); setErr(r.error || 'Could not check this account'); return; }
    setPreview(r.data);
    const m = r.data.account?.membership;
    if (!plan || asPay) setPlan(m && !m.is_trial ? m.plan_code : (r.data.plans?.[0]?.code || ''));
    // An assigned membership that was in fact paid for: keep its dates.
    if (m && !m.is_trial && m.source === 'manual') setStart('keep_current');
  };

  const pay = preview?.payment;
  const acct = preview?.account;
  const m = acct?.membership;
  const blocked = kind === 'razorpay' && (!pay || pay.status !== 'captured' || !!pay.on_file_for || pay.product === 'ip_intel');
  const canSubmit = !!preview && !!plan && !busy && !blocked
    && (kind === 'razorpay' || (Number(amount) > 0 && !!currency && !!method));

  const submit = async () => {
    if (!canSubmit) return;
    const what = kind === 'razorpay' && pay ? money(pay.amount, pay.currency)
      : `${currency} ${Number(amount).toLocaleString()}`;
    if (!window.confirm(`Record ${what} for ${acct.email} and make them a paid member?`)) return;
    setErr(''); setBusy(true);
    const r = await apiService.adminRecordManualPayment(uid, {
      email: acct.email, kind, plan_code: plan, start, months: start === 'keep_current' ? null : (months || null),
      note, notify,
      ...(kind === 'razorpay' ? { razorpay_payment_id: payId.trim() }
        : { method, amount, currency, reference, paid_on: paidOn || null }),
    });
    setBusy(false);
    if (!(r.success && r.data?.success)) { setErr(r.error || 'Could not record the payment'); return; }
    setDone(r.data); setPreview(null); setPayId(''); setAmount(''); setReference(''); setNote('');
    loadFound();
    onRecorded?.();
  };

  /** Fill the form from a listed payment and check it at once. */
  const use = (row: any) => {
    const em = row.suggested?.email || row.email || '';
    setOpen(true); setKind('razorpay'); setPayId(row.id); setEmail(em); setPreview(null); setPlan('');
    if (em) check(em, row.id);
    else setErr('No account matches this payer: type the account e-mail, then Check.');
  };
  const hide = async (row: any) => {
    if (!window.confirm(`Hide ${row.id} from this list? Use this for a payment that is not a membership.`)) return;
    const r = await apiService.adminHideUnmatchedPayment(uid, row.id, true);
    if (r.success && r.data?.success) loadFound(); else setErr(r.error || 'Could not hide the payment');
  };

  return (
    <div className="rounded-xl border border-grayscale-300 bg-white p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Record a manual payment</div>
          <div className="text-xs text-grayscale-1100 mt-0.5">
            Someone paid through a Razorpay link or outside Razorpay: tie the payment to their account and make them a paid member.
          </div>
        </div>
        <button type="button" onClick={() => setOpen(o => !o)}
          className="h-8 px-3 rounded-full border border-primary-300 text-xs font-medium text-primary-700 hover:bg-primary-100">
          {open ? 'Close' : 'Record a payment'}
        </button>
      </div>

      {/* Payments at Razorpay that no account holds, with a suggested account. */}
      <div className="mt-3 rounded-lg border border-grayscale-200">
        <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 bg-grayscale-100 rounded-t-lg">
          <div className="text-xs font-bold text-grayscale-1300">
            Razorpay payments not tied to an account{found?.rows ? ` (${found.rows.length})` : ''}
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              className="h-7 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-xs">
              {[30, 90, 180, 365].map(d => <option key={d} value={d}>Last {d} days</option>)}
            </select>
            <button type="button" onClick={loadFound} className="text-xs text-primary-700 underline underline-offset-2">Refresh</button>
          </div>
        </div>
        {found === null ? (
          <div className="px-3 py-2 text-xs text-grayscale-1100">Looking at Razorpay…</div>
        ) : found.error ? (
          <div className="px-3 py-2 text-xs text-red-700">{found.error}</div>
        ) : !found.rows.length ? (
          <div className="px-3 py-2 text-xs text-grayscale-1100">Every captured payment in this period is tied to an account.</div>
        ) : (
          <div className="divide-y divide-grayscale-200">
            {found.rows.map((x: any) => (
              <div key={x.id} className="px-3 py-2 flex items-start justify-between gap-3 flex-wrap text-xs">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-grayscale-1300">
                    {money(x.amount, x.currency)} <span className="text-xs font-normal text-grayscale-1100">· {fmtDay(x.paid_at)} · {x.method}</span>
                  </div>
                  <div className="text-grayscale-1200">
                    Payer: {[x.email, x.contact].filter(Boolean).join(' · ') || 'not given'}
                    <span className="font-mono text-[10px] text-grayscale-1100 ml-2">{x.id}</span>
                  </div>
                  <div className="mt-0.5">
                    {x.suggested ? (
                      <span className="text-green-800">
                        Suggested: {x.suggested.name ? `${x.suggested.name} · ` : ''}{x.suggested.email}
                        {x.suggested.state ? ` (${STATE_LABEL[x.suggested.state] || x.suggested.state})` : ''}
                        <span className="text-grayscale-1100"> — {x.match}</span>
                      </span>
                    ) : <span className="text-amber-800">No account matches this payer</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button type="button" onClick={() => use(x)}
                    className="h-7 px-3 rounded-full bg-primary-500 text-white text-xs font-medium hover:bg-primary-600">
                    {x.suggested ? 'Use suggestion' : 'Use'}
                  </button>
                  <button type="button" onClick={() => hide(x)} className="text-grayscale-1100 underline underline-offset-2">Hide</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {found && !found.error && (found.small_hidden > 0 || found.refunded_hidden > 0 || found.admin_hidden > 0) && (
          <div className="px-3 py-1.5 text-[11px] text-grayscale-1100 border-t border-grayscale-200">
            Not listed: {[found.small_hidden ? `${found.small_hidden} under ₹100 (card checks, tests)` : '',
              found.refunded_hidden ? `${found.refunded_hidden} fully refunded` : '',
              found.admin_hidden ? `${found.admin_hidden} hidden by the team` : ''].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      {done && (
        <div className="mt-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          Recorded {money(done.amount_minor, done.currency)} for {done.email}: {done.plan} until {fmtDay(done.access_until)}
          {done.emailed ? '; the paid welcome e-mail was sent.' : '.'}
        </div>
      )}

      {open && (
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 min-w-[16rem] flex-1">
              <span className={label}>Account e-mail</span>
              <input className={input} value={email} onChange={e => { setEmail(e.target.value); setPreview(null); }}
                placeholder="name@company.com" />
            </label>
            <div className="flex flex-col gap-1">
              <span className={label}>How they paid</span>
              <div className="flex gap-1">
                {([['razorpay', 'Razorpay payment'], ['outside', 'Outside Razorpay']] as const).map(([k, t]) => (
                  <button key={k} type="button" onClick={() => { setKind(k); setPreview(null); }}
                    className={`h-9 px-3 rounded-md text-xs font-medium border ${kind === k
                      ? 'bg-primary-100 border-primary-400 text-primary-700' : 'bg-white border-grayscale-300 text-grayscale-1200'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            {kind === 'razorpay' && (
              <label className="flex flex-col gap-1 min-w-[14rem]">
                <span className={label}>Razorpay payment id</span>
                <input className={`${input} font-mono`} value={payId} onChange={e => { setPayId(e.target.value); setPreview(null); }}
                  placeholder="pay_XXXXXXXXXXXXXX" />
              </label>
            )}
            <button type="button" onClick={() => check()} disabled={busy || !email.trim() || (kind === 'razorpay' && !payId.trim())}
              className="h-9 px-4 rounded-full bg-grayscale-1300 text-white text-sm font-medium disabled:opacity-50">
              {busy && !preview ? 'Checking…' : 'Check'}
            </button>
          </div>

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

          {preview && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-grayscale-200 p-3 text-sm">
                  <div className="text-xs text-grayscale-1100 mb-1">Account</div>
                  <div className="font-medium text-grayscale-1300">{acct.name || acct.email}</div>
                  <div className="text-xs text-grayscale-1200">{acct.email} · {STATE_LABEL[acct.state] || acct.state || '—'}</div>
                  <div className="text-xs text-grayscale-1200 mt-1">
                    {m ? <>Now: {m.plan_name} ({m.is_trial ? 'trial' : SOURCE_LABEL[m.source] || m.source}), {fmtDay(m.current_start)} → {fmtDay(m.current_end)}</>
                      : 'No live membership'}
                  </div>
                </div>
                {kind === 'razorpay' && pay && (
                  <div className="rounded-lg border border-grayscale-200 p-3 text-sm">
                    <div className="text-xs text-grayscale-1100 mb-1">Razorpay says</div>
                    <div className="font-medium text-grayscale-1300">{money(pay.amount, pay.currency)} · {pay.status}</div>
                    <div className="text-xs text-grayscale-1200">
                      {pay.method} · paid {fmtDay(pay.paid_at)}{pay.email ? ` · ${pay.email}` : ''}{pay.contact ? ` · ${pay.contact}` : ''}
                    </div>
                    {pay.order_id && <div className="text-[11px] font-mono text-grayscale-1100 mt-1">{pay.order_id}</div>}
                  </div>
                )}
              </div>
              {(preview.warnings || []).length > 0 && (
                <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 list-disc pl-6">
                  {preview.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                </ul>
              )}

              {kind === 'outside' && (
                <div className="flex flex-wrap gap-3">
                  <label className="flex flex-col gap-1">
                    <span className={label}>Method</span>
                    <select className={input} value={method} onChange={e => setMethod(e.target.value)}>
                      {Object.entries(preview.methods || {}).map(([k, t]) => <option key={k} value={k}>{String(t)}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 w-32">
                    <span className={label}>Amount</span>
                    <input className={input} inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="9000" />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={label}>Currency</span>
                    <select className={input} value={currency} onChange={e => setCurrency(e.target.value)}>
                      {(preview.currencies || []).map((c: string) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 min-w-[12rem] flex-1">
                    <span className={label}>Reference (optional)</span>
                    <input className={input} value={reference} onChange={e => setReference(e.target.value)} placeholder="UTR / invoice number" />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={label}>Paid on</span>
                    <input type="date" className={input} value={paidOn} onChange={e => setPaidOn(e.target.value)} />
                  </label>
                </div>
              )}

              <div className="flex flex-wrap gap-3 items-end">
                <label className="flex flex-col gap-1">
                  <span className={label}>Plan</span>
                  <select className={input} value={plan} onChange={e => setPlan(e.target.value)}>
                    {(preview.plans || []).map((p: any) => <option key={p.code} value={p.code}>{p.name}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className={label}>Access</span>
                  <select className={input} value={start} onChange={e => setStart(e.target.value as any)}>
                    <option value="today">From today</option>
                    <option value="payment_date">From the payment date</option>
                    {m && !m.is_trial && <option value="keep_current">Keep the current dates (until {fmtDay(m.current_end)})</option>}
                  </select>
                </label>
                {start !== 'keep_current' && (
                  <label className="flex flex-col gap-1 w-36">
                    <span className={label}>Months (blank = plan's period)</span>
                    <input className={input} inputMode="numeric" value={months} onChange={e => setMonths(e.target.value.replace(/\D/g, ''))} placeholder="12" />
                  </label>
                )}
              </div>

              <label className="flex flex-col gap-1">
                <span className={label}>Note (optional)</span>
                <textarea className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm min-h-[60px]"
                  value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. paid via payment link sent on WhatsApp, LOYAL50 price agreed" />
              </label>
              <label className="flex items-center gap-2 text-sm text-grayscale-1200">
                <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
                Send the paid welcome e-mail
              </label>

              <div className="flex items-center gap-3">
                <button type="button" onClick={submit} disabled={!canSubmit}
                  className="h-9 px-4 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
                  {busy ? 'Recording…' : 'Record payment and upgrade'}
                </button>
                <span className="text-xs text-grayscale-1100">They can add a card for auto-renewal later from Billing.</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ManualPaymentCard;
